import React from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Group as GroupIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { staggerContainer, staggerItem } from '../../animations/variants';

const GroupList = ({ groups, onGroupClick, isLoadingCounts = false, loading = false }) => {
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
      }}>
        <CircularProgress sx={{ color: '#D64794' }} />
      </Box>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
          py: 6,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: '#F0F4F8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <GroupIcon sx={{ fontSize: 40, color: '#BDBDBD' }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          No groups found
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#999999',
            fontFamily: 'Metropolis, sans-serif',
            textAlign: 'center',
            maxWidth: 300,
            fontWeight: 500,
          }}
        >
          Create your first group to get started
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate">
      <List sx={{ p: 0 }}>
        {groups.map((group, index) => (
          <motion.div key={group.id || group.name} variants={staggerItem}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => onGroupClick(group.name)}
                sx={{
                  py: 2,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#F9FBFD',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      backgroundColor: '#D64794',
                      width: 40,
                      height: 40,
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    {group.name ? group.name.charAt(0).toUpperCase() : 'G'}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: '#253E66',
                      }}
                    >
                      {group.name}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <GroupIcon
                        sx={{
                          fontSize: '0.875rem',
                          color: '#999999',
                        }}
                      />
                      {isLoadingCounts ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CircularProgress size={12} sx={{ color: '#D64794' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              color: '#999999',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                            }}
                          >
                            Loading...
                          </Typography>
                        </Box>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              color: '#666666',
                              fontSize: '0.8rem',
                            }}
                          >
                            {group.memberCount || 0} member
                            {(group.memberCount || 0) !== 1 ? 's' : ''}
                          </Typography>
                        </motion.div>
                      )}
                    </Box>
                  }
                />

                <ChevronRightIcon sx={{ color: '#BDBDBD' }} />
              </ListItemButton>
            </ListItem>

            {index < groups.length - 1 && (
              <Divider sx={{ borderColor: '#E5E5E5' }} />
            )}
          </motion.div>
        ))}
      </List>
    </motion.div>
  );
};

export default GroupList;
